import 'react-calendar/dist/Calendar.css';
import Layout from '../../components/layoutConfig/layout';
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import { setupAPIclient } from '../../services/setupAPIclient';
import LocalStorage from "../../helpers/localstorage";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FiTrash2, FiSave, FiPlusCircle, FiCheckCircle, FiSearch, FiMapPin, FiHome, FiHash, FiEdit, FiList 
} from 'react-icons/fi';

interface FormLocal {
  id: number;
  unit_id: number | null;
  unitName: string;
  name: string;
}

interface FormUnit {
  id: number;
  region_id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

function LocalForm() {
  const localStorage = new LocalStorage();
  const token = String(localStorage.getAuthToken());
  const [data, setData] = useState<FormLocal[]>([]);
  const [filterText, setFilterText] = useState("");
  const [pending, setPending] = useState(true);
  const [units, setUnits] = useState<FormUnit[]>([]);
  const [formLocal, setFormLocal] = useState({ name: "", unit_id: 0 });

  const ApiClient = setupAPIclient(token);

  const handleGetDataInput = async () => {
    try {
      const response = await ApiClient.post<{ success: boolean; data: any[] }>("/management/get/location");
      const allLocals: FormLocal[] = response.data.map((item) => ({
        id: item.id,
        unit_id: item.management_unit?.id ?? null,
        unitName: item.management_unit?.name ?? "",
        name: item.name ?? ""
      }));
      setData(allLocals);
      setPending(false);
    } catch (error) {
      console.error("Erro ao buscar locais:", error);
      toast.error("Erro ao carregar locais");
    }
  };

  const handleGetUnits = async () => {
    try {
      const response = await ApiClient.post<{ success: boolean; data: FormUnit[] }>("/management/get/many/unit");
      setUnits(response.data);
    } catch (error) {
      console.error("Erro ao buscar unidades:", error);
      toast.error("Erro ao carregar unidades");
    }
  };

  const handleUpdate = async (row: FormLocal) => {
    try {
      await ApiClient.post("/management/update/location", {
        id: row.id,
        name: row.name,
        unit_id: row.unit_id
      });
      toast.success("Local atualizado com sucesso");
      handleGetDataInput();
    } catch (error) {
      console.error("Erro ao atualizar local:", error);
      toast.error("Erro ao atualizar local");
    }
  };

  const handleCreate = async () => {
    if (!formLocal.name || !formLocal.unit_id) {
      toast.warn("Preencha todos os campos antes de cadastrar");
      return;
    }
    try {
      await ApiClient.post("/management/create/location", { ...formLocal });
      toast.success("Local cadastrado com sucesso");
      setFormLocal({ name: "", unit_id: 0 });
      handleGetDataInput();
    } catch (error) {
      console.error("Erro ao cadastrar local:", error);
      toast.error("Erro ao cadastrar local");
    }
  };

  const handleDelete = async (row: FormLocal) => {
    if (!window.confirm(`Tem certeza que deseja deletar o local ${row.name}?`)) return;
    try {
      const response = await ApiClient.post("/management/delete/location", { id: row.id });
      if (response.data.success === false) {
        toast.warning(`Não é possível deletar. Existem dados associados ao local ${row.name}`);
      } else {
        toast.success("Local deletado com sucesso");
        handleGetDataInput();
      }
    } catch (error) {
      console.error("Erro ao deletar local:", error);
      toast.warning(`Não é possível deletar. Existem dados associados ao local ${row.name}`);
    }
  };

  useEffect(() => {
    handleGetDataInput();
    handleGetUnits();
  }, []);

  const iconColor = "#5dade2"; // ícones atualizados
  const buttonColor = "#e74c3c"; // botões atualizados
  const iconSize = 22;

  const columns: TableColumn<FormLocal>[] = [
    {
      name: <div className="d-flex align-items-center gap-1"><FiHash color={iconColor} size={iconSize} /> ID</div>,
      selector: (row) => row.id,
      sortable: true,
      width: "70px",
    },
    {
      name: <div className="d-flex align-items-center gap-1"><FiHome color={iconColor} size={iconSize} /> Unidade</div>,
      selector: (row) => row.unitName,
      sortable: true,
      cell: (row) => (
        <select
          className="form-select form-select-sm"
          value={row.unit_id ?? ""}
          onChange={(e) =>
            setData((prev) =>
              prev.map((r) =>
                r.id === row.id
                  ? {
                      ...r,
                      unit_id: parseInt(e.target.value),
                      unitName: units.find((u) => u.id === parseInt(e.target.value))?.name ?? ""
                    }
                  : r
              )
            )
          }
        >
          <option value="">{row.unitName || "Selecione"}</option>
          {units.map((unit) => (
            <option key={unit.id} value={unit.id}>
              {unit.name}
            </option>
          ))}
        </select>
      ),
    },
    {
      name: <div className="d-flex align-items-center gap-1"><FiMapPin color={iconColor} size={iconSize} /> Local</div>,
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <input
          type="text"
          className="form-control form-control-sm"
          value={row.name ?? ""}
          onChange={(e) =>
            setData((prev) =>
              prev.map((r) => (r.id === row.id ? { ...r, name: e.target.value } : r))
            )
          }
        />
      ),
    },
    {
      name: <div className="d-flex align-items-center gap-1"><FiEdit color={iconColor} size={iconSize} /> Ações</div>,
      cell: (row) => (
        <div className="d-flex gap-2 justify-content-center">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="btn btn-md d-flex align-items-center gap-1"
            style={{ borderColor: buttonColor, color: buttonColor }}
            onClick={() => handleUpdate(row)}
          >
            <FiSave color={iconColor} /> 
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="btn btn-md d-flex align-items-center gap-1"
            style={{ borderColor: buttonColor, color: buttonColor }}
            onClick={() => handleDelete(row)}
          >
            <FiTrash2 color={iconColor} /> 
          </motion.button>
        </div>
      ),
      center: true,
      width: "180px",
    },
  ];

  const filteredItems = data.filter((item) =>
    item.name?.toLowerCase().includes(filterText.toLowerCase())
  );

  const totalLocals = data.length;
  const totalUnits = units.length;

  return (
    <Layout>
      <div className="mb-4">
        <h4 className="d-flex align-items-center gap-2">
          <FiMapPin color={iconColor} size={26} /> Configuração de Locais
        </h4>
        <div className="d-flex gap-3 mt-3 flex-wrap">
          <div className="card flex-fill p-3 shadow-sm">
            <div className="d-flex align-items-center gap-2">
              <FiList color={iconColor} size={22} />
              <div>
                <div>Total de Locais</div>
                <strong>{totalLocals}</strong>
              </div>
            </div>
          </div>
          <div className="card flex-fill p-3 shadow-sm">
            <div className="d-flex align-items-center gap-2">
              <FiHome color={iconColor} size={22} />
              <div>
                <div>Total de Unidades</div>
                <strong>{totalUnits}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      <motion.div className="card p-4 mb-4 shadow-sm" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
        <h5 className="mb-3 d-flex align-items-center gap-2">
          <FiPlusCircle color={iconColor} size={iconSize} /> Novo Local
        </h5>
        <div className="row align-items-end g-3">
          <div className="col-md-4">
            <label className="form-label">Nome do Local</label>
            <input type="text" className="form-control" placeholder="Digite o nome do local" value={formLocal.name} onChange={(e) => setFormLocal({ ...formLocal, name: e.target.value })} />
          </div>
          <div className="col-md-4">
            <label className="form-label">Unidade</label>
            <select className="form-select" value={formLocal.unit_id} onChange={(e) => setFormLocal({ ...formLocal, unit_id: parseInt(e.target.value) })}>
              <option value="">Selecione a unidade</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>{unit.name}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2 d-grid">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="btn d-flex align-items-center justify-content-center gap-1" style={{ borderColor: buttonColor, color: buttonColor }} onClick={handleCreate}>
              <FiCheckCircle color={iconColor} /> Cadastrar
            </motion.button>
          </div>
        </div>
      </motion.div>

      <div className="mb-3">
        <div className="input-group" style={{ maxWidth: "300px" }}>
          <span className="input-group-text bg-white"><FiSearch color={iconColor} /></span>
          <input type="text" className="form-control" placeholder="Pesquisar local..." value={filterText} onChange={(e) => setFilterText(e.target.value)} />
        </div>
      </div>

      <DataTable columns={columns} data={filteredItems} progressPending={pending} pagination highlightOnHover pointerOnHover responsive striped dense noHeader />
    </Layout>
  );
}

export default LocalForm;
